import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { refresh as refreshApi, logout as logoutApi } from '../api/auth';
import { getAccessToken as getToken, setAccessToken as setToken, subscribe, setOnRefreshFailed } from './tokenStore';

type AuthContextValue = {
  accessToken: string | null;
  setAccessToken: (t: string | null) => void;
  clearAuth: () => Promise<void>;
  isAuthenticated: boolean;
  isInitializing: boolean;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const sessionKey = 'auth-session';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [accessToken, setAccessTokenState] = useState<string | null>(getToken());
  const [isInitializing, setIsInitializing] = useState(true);
  const navigate = useNavigate();

  const setAccessToken = (t: string | null) => {
    setToken(t);
    setAccessTokenState(t);
    if (t) {
      sessionStorage.setItem(sessionKey, 'true');
    } else {
      sessionStorage.removeItem(sessionKey);
    }
  };

  const clearAuth = async () => {
    try {
      await logoutApi();
    } catch (e) {
      // ignore
    } finally {
      setAccessToken(null);
      navigate('/login');
    }
  };

  useEffect(() => {
    // subscribe to token store changes
    const unsub = subscribe((t) => setAccessTokenState(t));
    // handle refresh failures triggered outside React (axios interceptor)
    setOnRefreshFailed(() => {
      setAccessToken(null);
      navigate('/login');
    });

    let mounted = true;
    if (sessionStorage.getItem(sessionKey) !== 'true') {
      setIsInitializing(false);
      return () => {
        unsub();
        mounted = false;
      };
    }

    // Restore a session only when a previous authenticated session exists.
    (async () => {
      try {
        const resp = await refreshApi();
        if (!mounted) return;
        const newToken = resp.data?.accessToken ?? null;
        if (newToken) setAccessToken(newToken);
      } catch (e) {
        // leave unauthenticated
        setAccessToken(null);
      } finally {
        setIsInitializing(false);
      }
    })();

    return () => {
      unsub();
      mounted = false;
    };
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ accessToken, setAccessToken, clearAuth, isAuthenticated: !!accessToken, isInitializing }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export { getToken as getAccessToken };

export default AuthProvider;
